import React, { useState, useEffect } from 'react';
import { FaSync, FaSearch, FaFilter, FaTimes, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { 
  getAllTournaments, 
  createTournament, 
  updateTournament, 
  deleteTournament,
  getActivityTypes,
  Tournament, 
  CreateTournamentRequest, 
  UpdateTournamentRequest,
  formatDateArray,
  ActivityType
} from '../../services/tournamentService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import './tournament.css'; // Import CSS riêng cho trang giải đấu
import TournamentConfigPanel from '../../components/TournamentConfig/TournamentConfigPanel';

// Interface cho option của Select
interface ActivityTypeOption {
  value: number;
  label: string;
}

const Tournaments: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]); 
  const [activityTypeOptions, setActivityTypeOptions] = useState<ActivityTypeOption[]>([]);
  const [loadingActivityTypes, setLoadingActivityTypes] = useState(false);
  
  // State cho modal tạo mới
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState<CreateTournamentRequest>({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    imageUrl: '',
    activityTypeIds: [],
    visible: true,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  
  // State cho modal cập nhật
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [updateData, setUpdateData] = useState<UpdateTournamentRequest>({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    imageUrl: '',
    status: '',
    activityTypeIds: [],
    visible: true,
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  
  // State cho modal xem chi tiết
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // State cho modal cấu hình giải đấu
  const [showConfigModal, setShowConfigModal] = useState(false);
  
  const navigate = useNavigate();
  
  // Kiểm tra quyền admin
  useEffect(() => {
    setIsUserAdmin(isAdmin());
  }, []);
  
  // Lấy dữ liệu giải đấu
  const fetchTournaments = async () => {
    try {
      setLoading(true);
      // Truyền true vào hàm getAllTournaments để chỉ hiển thị giải đấu visible
      // cho người dùng thông thường ở trang Tournaments
      const data = await getAllTournaments(true);
      setTournaments(data);
      setError(null);
    } catch (err: any) {
      console.error('Lỗi khi lấy dữ liệu giải đấu:', err);
      setError(err?.message || 'Không thể lấy dữ liệu giải đấu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };
  
  // Lấy danh sách loại hoạt động
  const fetchActivityTypes = async () => {
    try {
      setLoadingActivityTypes(true);
      const data = await getActivityTypes();
      setActivityTypes(data);
      // Chuyển đổi dữ liệu sang định dạng cho react-select
      setActivityTypeOptions(data.map(type => ({
        value: type.id,
        label: type.name
      })));
    } catch (err: any) {
      console.error('Lỗi khi lấy loại hoạt động:', err);
    } finally {
      setLoadingActivityTypes(false);
    }
  };
  
  // Lấy dữ liệu khi component mount
  useEffect(() => {
    fetchTournaments();
    fetchActivityTypes();
  }, []);
  
  // Tìm kiếm giải đấu
  const filteredTournaments = searchQuery.trim() === '' 
    ? tournaments 
    : tournaments.filter(tournament => 
        tournament.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
  // Định dạng ngày tháng
  const formatDate = (dateValue: string | number[]) => {
    if (Array.isArray(dateValue)) {
      return formatDateArray(dateValue);
    }
    const date = new Date(dateValue);
    return date.toLocaleDateString('vi-VN');
  };
  
  // Xử lý thay đổi input form tạo mới
  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Xử lý thay đổi input form cập nhật
  const handleUpdateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUpdateData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Xử lý thay đổi loại hoạt động trong form tạo mới
  const handleCreateActivityTypeChange = (options: readonly ActivityTypeOption[]) => {
    setCreateData(prev => ({
      ...prev,
      activityTypeIds: options.map(option => option.value)
    }));
  };
  
  // Xử lý thay đổi loại hoạt động trong form cập nhật
  const handleUpdateActivityTypeChange = (options: readonly ActivityTypeOption[]) => {
    setUpdateData(prev => ({
      ...prev,
      activityTypeIds: options.map(option => option.value)
    }));
  };
  
  // Hàm tạo giải đấu mới
  const handleCreateTournament = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    
    try {
      // Kiểm tra đầu vào
      if (!createData.name || !createData.startDate || !createData.endDate) {
        setCreateError('Vui lòng điền đầy đủ thông tin');
        setCreateLoading(false);
        return;
      }
      
      // Kiểm tra có chọn loại hoạt động chưa
      if (!createData.activityTypeIds || createData.activityTypeIds.length === 0) {
        setCreateError('Vui lòng chọn ít nhất một loại hoạt động');
        setCreateLoading(false);
        return;
      }
      
      // Gọi API tạo giải đấu mới
      await createTournament(createData);
      
      // Cập nhật lại danh sách và đóng modal
      fetchTournaments();
      setShowCreateModal(false);
      
      // Reset form
      setCreateData({
        name: '',
        startDate: '',
        endDate: '',
        description: '',
        imageUrl: '',
        activityTypeIds: [],
        visible: true,
      });
    } catch (err: any) {
      setCreateError(err?.message || 'Có lỗi xảy ra khi tạo giải đấu');
    } finally {
      setCreateLoading(false);
    }
  };
  
  // Hàm mở modal cập nhật
  const handleShowUpdateModal = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    const activityTypeIds = tournament.allowedActivityTypes?.map(type => type.id) || [];
    setUpdateData({
      name: tournament.name,
      startDate: formatDate(tournament.startDate),
      endDate: formatDate(tournament.endDate),
      description: tournament.description,
      imageUrl: tournament.imageUrl,
      status: tournament.status,
      activityTypeIds: activityTypeIds,
      visible: tournament.visible !== false
    });
    setShowUpdateModal(true);
  };
  
  // Hàm cập nhật giải đấu
  const handleUpdateTournament = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTournament) return;
    
    setUpdateLoading(true);
    setUpdateError(null);
    
    try {
      // Kiểm tra đầu vào
      if (!updateData.name || !updateData.startDate || !updateData.endDate) {
        setUpdateError('Vui lòng điền đầy đủ thông tin');
        setUpdateLoading(false);
        return;
      }
      // Gọi API cập nhật giải đấu
      await updateTournament(selectedTournament.id, updateData);
      
      // Cập nhật lại danh sách và đóng modal
      fetchTournaments();
      setShowUpdateModal(false);
    } catch (err: any) {
      setUpdateError(err?.message || 'Có lỗi xảy ra khi cập nhật giải đấu');
    } finally {
      setUpdateLoading(false);
    }
  };
  
  // Hàm xóa giải đấu
  const handleDeleteTournament = async (tournamentId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa giải đấu này không?')) {
      return;
    }
    
    try {
      await deleteTournament(tournamentId);
      fetchTournaments();
    } catch (err: any) {
      alert(`Lỗi khi xóa giải đấu: ${err?.message || 'Có lỗi xảy ra'}`);
    }
  };
  
  // Hàm xem chi tiết giải đấu
  const handleViewDetail = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setShowDetailModal(true);
  };
  
  // Hàm chuyển hướng đến trang xếp hạng tỷ lệ duy trì
  const navigateToRankings = (tournamentId: number) => {
    navigate(`/tournaments/${tournamentId}/rankings`);
  };
  
  // Hiển thị danh sách giải đấu dạng card
  const renderTournamentList = () => {
    if (filteredTournaments.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">🏆</div>
          <h3 className="empty-state-title">Không tìm thấy giải đấu nào</h3>
          <p className="empty-state-text">
            Không có giải đấu nào khớp với tìm kiếm của bạn hoặc chưa có giải đấu nào được tạo.
          </p>
        </div>
      );
    }
    
    return (
      <div className="tournament-grid">
        {filteredTournaments.map((tournament) => (
          <div key={tournament.id} className="tournament-card">
            <div 
              className="tournament-image" 
              style={{ backgroundImage: tournament.imageUrl ? `url(${tournament.imageUrl})` : 'none' }}
            >
              <div className={`tournament-status ${getTournamentStatusClass(tournament.status)}`}>
                {getTournamentStatusText(tournament.status)}
              </div>
            </div>
            
            {/* Chỉ hiển thị trạng thái ẩn/hiện cho admin */}
            {isUserAdmin && (
              <div className={`tournament-visibility ${tournament.visible ? 'visible' : 'hidden'}`}>
                {tournament.visible ? 'Hiển thị' : 'Đã ẩn'}
              </div>
            )}

            <div className="tournament-content">
              <div className="tournament-name">{tournament.name}</div>
              <div className="tournament-dates">
                <i className="calendar-icon"></i>
                {formatDate(tournament.startDate)} - {formatDate(tournament.endDate)}
              </div>
              <div className="tournament-description">
                {tournament.description || 'Chưa có mô tả cho giải đấu này.'}
              </div>
            </div>
            
            <div className="tournament-actions">
              <button 
                className="tournament-action-btn view"
                title="Xem chi tiết"
                onClick={() => handleViewDetail(tournament)}
              >
                <i className="view-icon"></i>
              </button>
              
              {/* Chỉ hiển thị các nút quản lý cho admin */}
              {isUserAdmin && (
                <>
                  <button
                    className="tournament-action-btn edit"
                    title="Sửa giải đấu"
                    onClick={() => handleShowUpdateModal(tournament)}
                  >
                    <i className="edit-icon"></i>
                  </button>
                  <button
                    className="tournament-action-btn toggle-visibility"
                    title={tournament.visible ? "Ẩn giải đấu" : "Hiển thị giải đấu"}
                    onClick={() => handleToggleVisibility(tournament.id, !tournament.visible)}
                  >
                    <i className={tournament.visible ? "hide-icon" : "show-icon"}></i>
                  </button>
                  <button
                    className="tournament-action-btn config"
                    title="Cấu hình giải đấu"
                    onClick={() => {
                      setSelectedTournament(tournament);
                      setShowConfigModal(true);
                    }}
                  >
                    <i className="config-icon"></i>
                  </button>
                  <button
                    className="tournament-action-btn delete"
                    title="Xóa giải đấu"
                    onClick={() => handleDeleteTournament(tournament.id)}
                  >
                    <i className="delete-icon"></i>
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Hàm lấy class cho trạng thái giải đấu
  const getTournamentStatusClass = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'ONGOING':
        return 'status-active';
      case 'UPCOMING':
        return 'status-upcoming';
      case 'COMPLETED':
        return 'status-ended';
      default:
        return '';
    }
  };
  
  // Hàm lấy text cho trạng thái giải đấu
  const getTournamentStatusText = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'ONGOING':
        return 'Đang diễn ra';
      case 'UPCOMING':
        return 'Sắp diễn ra';
      case 'COMPLETED':
        return 'Đã kết thúc';
      default:
        return status || 'Không xác định';
    }
  };
  
  // Thêm hàm xử lý bật/tắt hiển thị giải đấu
  const handleToggleVisibility = async (tournamentId: number, visible: boolean) => {
    try {
      // Lấy thông tin giải đấu hiện tại
      const tournament = tournaments.find(t => t.id === tournamentId);
      if (!tournament) return;
      
      // Chuẩn bị dữ liệu cập nhật, giữ nguyên các trường khác
      const updateData: UpdateTournamentRequest = {
        name: tournament.name,
        startDate: formatDate(tournament.startDate),
        endDate: formatDate(tournament.endDate),
        description: tournament.description,
        imageUrl: tournament.imageUrl || '',
        status: tournament.status,
        activityTypeIds: tournament.allowedActivityTypes?.map(t => t.id),
        visible: visible
      };
      
      // Gọi API cập nhật
      await updateTournament(tournamentId, updateData);
      
      // Cập nhật lại danh sách giải đấu
      fetchTournaments();
      
      // Hiển thị thông báo
      alert(visible ? 'Đã hiển thị giải đấu' : 'Đã ẩn giải đấu');
    } catch (error: any) {
      console.error('Lỗi khi cập nhật trạng thái hiển thị:', error);
      alert(`Lỗi: ${error.message || 'Không thể cập nhật trạng thái hiển thị'}`);
    }
  };
  
  return (
    <div className="tournaments-page">
      <div className="page-header">
        <h2 className="page-title">GIẢI ĐẤU</h2>
        <button 
          onClick={fetchTournaments}
          className={`btn-refresh ${loading ? 'loading' : ''}`}
          disabled={loading}
          title="Làm mới dữ liệu"
        >
          <FaSync />
        </button>
      </div>
      
      <div className="search-and-actions">
        <div className="teams-search-container">
          <FaSearch className="teams-search-icon" />
          <input
            type="text"
            placeholder="Tìm kiếm giải đấu theo tên..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="teams-search-input"
          />
          {searchQuery && (
            <button 
              className="search-clear-button" 
              onClick={() => setSearchQuery('')}
              title="Xóa tìm kiếm"
            >
              <FaTimes />
            </button>
          )}
        </div>
        
        {/* Chỉ hiển thị nút tạo mới cho admin */}
        {isUserAdmin && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-create"
          >
            <FaPlus /> Tạo giải đấu mới
          </button>
        )}
      </div>
      
      {/* Hiển thị lỗi nếu có */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {loading && tournaments.length === 0 ? (
        <div className="loading-message">Đang tải dữ liệu...</div>
      ) : (
        renderTournamentList()
      )}
      
      {/* Modal tạo mới giải đấu */}
      {showCreateModal && (
        <div 
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Tạo giải đấu mới</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            
            {createError && (
              <div className="modal-error">{createError}</div>
            )}
            
            <form onSubmit={handleCreateTournament}>
              <div className="form-group">
                <label className="form-label">Tên giải đấu *</label>
                <input
                  type="text"
                  name="name"
                  value={createData.name}
                  onChange={handleCreateInputChange}
                  className="form-input"
                  placeholder="Nhập tên giải đấu"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Ngày bắt đầu *</label>
                <input
                  type="date"
                  name="startDate"
                  value={createData.startDate}
                  onChange={handleCreateInputChange}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Ngày kết thúc *</label>
                <input
                  type="date"
                  name="endDate"
                  value={createData.endDate}
                  onChange={handleCreateInputChange}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Mô tả</label>
                <textarea
                  name="description"
                  value={createData.description}
                  onChange={(e) => setCreateData(prev => ({...prev, description: e.target.value}))}
                  className="form-textarea"
                  placeholder="Nhập mô tả cho giải đấu"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">URL Hình ảnh</label>
                <input
                  type="text"
                  name="imageUrl"
                  value={createData.imageUrl}
                  onChange={handleCreateInputChange}
                  className="form-input"
                  placeholder="Nhập URL hình ảnh (tùy chọn)"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Loại hoạt động cho phép *</label>
                <div className="activity-types-container">
                  {loadingActivityTypes ? (
                    <div className="loading-message">Đang tải...</div>
                  ) : (
                    <Select
                      isMulti
                      name="activityTypeIds"
                      options={activityTypeOptions}
                      value={createData.activityTypeIds?.map(id => ({
                        value: id,
                        label: activityTypes.find(type => type.id === id)?.name || ''
                      })) || []}
                      onChange={handleCreateActivityTypeChange}
                      placeholder="Tìm kiếm và chọn loại hoạt động..."
                      classNamePrefix="activity-select"
                      noOptionsMessage={() => "Không tìm thấy loại hoạt động nào"}
                    />
                  )}
                  {(createData.activityTypeIds?.length || 0) === 0 && (
                    <div className="form-hint error">Vui lòng chọn ít nhất một loại hoạt động</div>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="visible"
                    checked={createData.visible !== false}
                    onChange={(e) => setCreateData(prev => ({
                      ...prev,
                      visible: e.target.checked
                    }))}
                  />
                  Hiển thị giải đấu cho người dùng
                </label>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                >
                  Hủy bỏ
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={createLoading || (createData.activityTypeIds?.length || 0) === 0}
                >
                  {createLoading ? 'Đang xử lý...' : 'Tạo giải đấu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal xem chi tiết giải đấu */}
      {showDetailModal && selectedTournament && (
        <div className="modal-overlay">
          <div className="detail-modal">
            <div className="modal-header">
              <h2 className="modal-title">Chi tiết giải đấu</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                &times;
              </button>
            </div>
            
            <div className="detail-content">
              {/* Ảnh giải đấu */}
              {selectedTournament.imageUrl && (
                <div 
                  className="detail-image" 
                  style={{ 
                    backgroundImage: `url(${selectedTournament.imageUrl})`,
                    height: '200px',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    marginBottom: '20px',
                    borderRadius: '8px'
                  }}
                ></div>
              )}
              
              <h3 className="detail-title">{selectedTournament.name}</h3>
              
              <div className="detail-info">
                <div className="detail-row">
                  <div className={`detail-status ${getTournamentStatusClass(selectedTournament.status)}`}>
                    {getTournamentStatusText(selectedTournament.status)}
                  </div>

                  {/* Hiển thị trạng thái ẩn/hiện cho admin */}
                  {isUserAdmin && (
                    <div className={`detail-visibility ${selectedTournament.visible !== false ? 'visible' : 'hidden'}`}>
                      {selectedTournament.visible !== false ? 'Hiển thị' : 'Đã ẩn'}
                    </div>
                  )}
                </div>
                
                <div className="detail-row">
                  <div className="detail-dates">
                    <span className="detail-label">Thời gian:</span>
                    {formatDate(selectedTournament.startDate)} - {formatDate(selectedTournament.endDate)}
                  </div>
                </div>

                <div className="detail-row">
                  <div className="detail-description">
                    <span className="detail-label">Mô tả:</span>
                    <p>{selectedTournament.description || 'Không có mô tả.'}</p>
                  </div>
                </div>
                
                {selectedTournament.allowedActivityTypes && selectedTournament.allowedActivityTypes.length > 0 && (
                  <div className="detail-row">
                    <div className="detail-activity-types">
                      <span className="detail-label">Loại hoạt động được phép:</span>
                      <div className="activity-type-list">
                        {selectedTournament.allowedActivityTypes.map(type => (
                          <span key={type.id} className="activity-type-tag">
                            {type.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="detail-row">
                  <div className="detail-teams">
                    <span className="detail-label">Số lượng đội:</span>
                    <span>{selectedTournament.teamCount || 0}</span>
                  </div>
                </div>
                
                <div className="detail-row">
                  <div className="detail-created">
                    <span className="detail-label">Người tạo:</span>
                    <span>{selectedTournament.createdByName || 'Không có thông tin'}</span>
                  </div>
                </div>
                
                <div className="detail-row">
                  <div className="detail-created-date">
                    <span className="detail-label">Ngày tạo:</span>
                    <span>{formatDate(selectedTournament.createdAt)}</span>
                  </div>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-view-rankings" 
                  onClick={() => navigateToRankings(selectedTournament.id)}
                >
                  Xem xếp hạng
                </button>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                  Đóng
                </button>
                {/* Hiển thị các nút quản lý cho admin */}
                {isUserAdmin && (
                  <>
                    <button 
                      className="btn-edit" 
                      onClick={() => {
                        setShowDetailModal(false);
                        handleShowUpdateModal(selectedTournament);
                      }}
                    >
                      Sửa thông tin
                    </button>
                    <button 
                      className="btn-toggle-visibility" 
                      onClick={() => {
                        handleToggleVisibility(selectedTournament.id, !selectedTournament.visible);
                        setShowDetailModal(false);
                      }}
                    >
                      {selectedTournament.visible !== false ? 'Ẩn giải đấu' : 'Hiển thị giải đấu'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal chỉnh sửa giải đấu */}
      {showUpdateModal && selectedTournament && (
        <div 
          className="modal-overlay"
          onClick={() => setShowUpdateModal(false)}
        >
          <div 
            className="modal-container update-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header update-header">
              <h3 className="modal-title">Chỉnh sửa giải đấu</h3>
              <button 
                className="modal-close"
                onClick={() => setShowUpdateModal(false)}
              >
                ×
              </button>
            </div>
            
            {updateError && (
              <div className="modal-error">
                <div className="error-icon">⚠️</div>
                <div className="error-message">{updateError}</div>
              </div>
            )}
            
            <form onSubmit={handleUpdateTournament} className="update-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tên giải đấu <span className="required-mark">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={updateData.name}
                    onChange={handleUpdateInputChange}
                    className="form-input"
                    placeholder="Nhập tên giải đấu"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Ngày bắt đầu <span className="required-mark">*</span></label>
                  <input
                    type="date"
                    name="startDate"
                    value={updateData.startDate}
                    onChange={handleUpdateInputChange}
                    className="form-input date-input"
                    required
                  />
                </div>
              </div>
                
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Ngày kết thúc <span className="required-mark">*</span></label>
                  <input
                    type="date"
                    name="endDate"
                    value={updateData.endDate}
                    onChange={handleUpdateInputChange}
                    className="form-input date-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Trạng thái <span className="required-mark">*</span></label>
                  <select
                    name="status"
                    value={updateData.status}
                    onChange={(e) => setUpdateData(prev => ({...prev, status: e.target.value}))}
                    className="form-input status-select"
                    required
                  >
                    <option value="">-- Chọn trạng thái --</option>
                    <option value="UPCOMING">Sắp diễn ra</option>
                    <option value="ONGOING">Đang diễn ra</option>
                    <option value="COMPLETED">Đã kết thúc</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Mô tả</label>
                  <textarea
                    name="description"
                    value={updateData.description}
                    onChange={(e) => setUpdateData(prev => ({...prev, description: e.target.value}))}
                    className="form-textarea"
                    placeholder="Nhập mô tả cho giải đấu"
                    rows={4}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">URL Hình ảnh</label>
                  <div className="image-input-container">
                    <input
                      type="text"
                      name="imageUrl"
                      value={updateData.imageUrl}
                      onChange={handleUpdateInputChange}
                      className="form-input"
                      placeholder="Nhập URL hình ảnh (tùy chọn)"
                    />
                    {updateData.imageUrl && (
                      <div className="image-preview-badge" title="Có hình ảnh">🖼️</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Loại hoạt động cho phép <span className="required-mark">*</span></label>
                  <div className="activity-types-container">
                    {loadingActivityTypes ? (
                      <div className="loading-message">Đang tải...</div>
                    ) : (
                      <Select
                        isMulti
                        name="activityTypeIds"
                        options={activityTypeOptions}
                        value={updateData.activityTypeIds?.map(id => ({
                          value: id,
                          label: activityTypes.find(type => type.id === id)?.name || ''
                        })) || []}
                        onChange={handleUpdateActivityTypeChange}
                        placeholder="Tìm kiếm và chọn loại hoạt động..."
                        classNamePrefix="activity-select"
                        noOptionsMessage={() => "Không tìm thấy loại hoạt động nào"}
                        className="activity-select-container"
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="visible"
                    checked={updateData.visible !== false}
                    onChange={(e) => setUpdateData(prev => ({
                      ...prev,
                      visible: e.target.checked
                    }))}
                  />
                  Hiển thị giải đấu cho người dùng
                </label>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowUpdateModal(false)}
                >
                  Hủy bỏ
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={updateLoading}
                >
                  {updateLoading ? (
                    <><span className="spinner"></span>Đang xử lý...</>
                  ) : (
                    'Cập nhật giải đấu'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal cấu hình giải đấu */}
      {showConfigModal && selectedTournament && (
        <TournamentConfigPanel
          tournamentId={selectedTournament.id}
          activityTypes={selectedTournament.allowedActivityTypes || []}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </div>
  );
};

export default Tournaments; 